import React, { useEffect, useState } from 'react';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import {
  IScene,
  ISelectedCustomScenes
} from '../../../types/Scenes/scenesTypes';
import Strings from '../../../i18n';

interface CustomLightShowProps {
  scenes: IScene[];
  selectedScenes: ISelectedCustomScenes[];
  setSelectedScenes: (selectedScenes: ISelectedCustomScenes[]) => void;
}

const TransitionStylesIds = [
  {
    id: 'NO_TRANSITION',
    name: Strings.lightShows.noTransition
  },
  {
    id: 'BLEND',
    name: Strings.lightShows.blend
  },
  {
    id: 'FADE',
    name: Strings.lightShows.fadeInFadeOut
  }
];

const defaultConfiguration = {
  sceneId: 0,
  name: '',
  transitionType: 'NO_TRANSITION',
  transitionTypeDescription: Strings.lightShows.noTransition,
  transitionDuration: 0,
  transitionDurationDetails: { seconds: 0, milliseconds: 0 },
  duration: 5,
  sceneDuration: { seconds: 0, milliseconds: 5 }
};

export const AdvanceLightShow = ({
  scenes,
  selectedScenes,
  setSelectedScenes
}: CustomLightShowProps) => {
  useEffect(() => {
    setSelectedScenes([{ ...defaultConfiguration }]);
  }, []);

  const handleAddScene = () => {
    setSelectedScenes([...selectedScenes, { ...defaultConfiguration }]);
  };

  const handleDeleteScene = (index: number) => {
    const newScenes = selectedScenes.filter((_, i) => i !== index);
    setSelectedScenes(newScenes);
  };

  const onChangeSceneDuration = (
    value: {
      seconds: number;
      milliseconds: number;
    },
    index: number
  ) => {
    const newScenesData = selectedScenes.map(
      (scene: ISelectedCustomScenes, sceneIndex: number) => {
        if (sceneIndex !== index) return scene;
        return {
          ...scene,
          duration: value.seconds + value.milliseconds / 10,
          sceneDuration: { ...value }
        };
      }
    );
    setSelectedScenes(newScenesData);
  };

  const onChangeTransitionDuration = (
    value: {
      seconds: number;
      milliseconds: number;
    },
    index: number
  ) => {
    const newScenes = selectedScenes.map((scene, sceneIndex) => {
      if (sceneIndex !== index) return scene;
      return {
        ...scene,
        transitionDuration: value.seconds + value.milliseconds / 10,
        transitionDurationDetails: { ...value }
      };
    });
    setSelectedScenes(newScenes);
  };

  const onChangeTransitionStyle = (value: string, index: number) => {
    const newScenes = selectedScenes.map((scene, sceneIndex) => {
      if (sceneIndex !== index) return scene;
      return {
        ...scene,
        transitionType:
          TransitionStylesIds.find((f) => f.name === value)?.id || '',
        transitionDuration:
          value === Strings.lightShows.noTransition
            ? 0
            : scene.transitionDuration,
        transitionTypeDescription: value,
        transitionDurationDetails:
          value === Strings.lightShows.noTransition
            ? { seconds: 0, milliseconds: 0 }
            : scene.transitionDurationDetails
      };
    });
    setSelectedScenes(newScenes);
  };

  const onSelectScene = (sceneId: number, index: number) => {
    const sceneData = scenes.find((tmpScene) => tmpScene.sceneId === sceneId);
    const newScenes = selectedScenes.map((scene, sceneIndex) => {
      if (sceneIndex !== index) return scene;
      return {
        ...scene,
        sceneId,
        name: sceneData?.name ?? ''
      };
    });
    setSelectedScenes(newScenes);
  };

  const renderDurationInput = (
    value: { seconds: number; milliseconds: number },
    onChange: (
      newValue: { seconds: number; milliseconds: number },
      index: number
    ) => void,
    index: number
  ) => (
    <div className="form-group">
      <div className="row">
        <div className="col">
          <input
            id="sceneDurationSeconds"
            type="number"
            value={value.seconds}
            onChange={(e) =>
              Number(e.target.value) >= 0 &&
              onChange({ ...value, seconds: Number(e.target.value) }, index)
            }
            className="form-control"
          />
          <small className="form-text text-muted">SEC</small>
        </div>
        <div className="col">
          <input
            type="number"
            value={value.milliseconds}
            onChange={(e) =>
              Array.from({ length: 10 }, (_, i) => i + 1).includes(
                Number(e.target.value)
              ) &&
              onChange(
                { ...value, milliseconds: Number(e.target.value) },
                index
              )
            }
            className="form-control"
          />
          <small className="form-text text-muted">1/10</small>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12 d-flex justify-content-center">
          <h5 className="text-center mb-0">* ADVANCE LIGHT SHOW SETTINGS</h5>
          <button
            type="button"
            onClick={handleAddScene}
            disabled={scenes.length >= 5}
            className="btn btn-primary"
          >
            +
          </button>
        </div>
      </div>

      <div className="row">
        {selectedScenes.length > 0 &&
          selectedScenes.map((scene, index) => (
            <div className="col-md-6">
              <div className="p-3 border rounded">
                <div className="flex-grow-1 d-flex justify-content-center">
                  <Dropdown
                    value={scene.sceneId}
                    options={scenes}
                    onChange={(e: DropdownChangeEvent) =>
                      onSelectScene(e.value, index)
                    }
                    optionLabel="name"
                    optionValue="sceneId"
                    placeholder="Select a Scene"
                    className="w-100"
                  />
                  {index >= 1 && (
                    <div className="input-group-append">
                      <button
                        type="button"
                        onClick={() => handleDeleteScene(index)}
                        className="btn btn-danger"
                      >
                        🗑️
                      </button>
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <h5>Scene Duration</h5>
                  {renderDurationInput(
                    scene.sceneDuration ?? { seconds: 0, milliseconds: 0 },
                    onChangeSceneDuration,
                    index
                  )}
                </div>
                <div className="mb-3">
                  <h5>Select Transition</h5>
                  <select
                    value={scene.transitionTypeDescription}
                    onChange={(e) =>
                      onChangeTransitionStyle(e.target.value, index)
                    }
                    className="form-control"
                  >
                    <option>No Transition</option>
                    <option>Blend</option>
                    <option>Fade In / Fade Out</option>
                  </select>
                </div>
                {scene.transitionTypeDescription !== 'No Transition' && (
                  <div className="mb-3">
                    <h5>Transition Duration</h5>
                    {renderDurationInput(
                      scene.transitionDurationDetails ?? {
                        seconds: 0,
                        milliseconds: 0
                      },
                      onChangeTransitionDuration,
                      index
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};
